import { StrictMode, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import { AuthProvider } from '@/features/auth/AuthContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';
import { checkAndClearStaleCache } from '@/features/auth/msalInstance.ts';
import { RouterProvider } from '@tanstack/react-router';
import { router } from '@/router';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '@/features/auth/useAuth';
import { AUTH_REDIRECT_KEY } from '@/constants';

// Ensure stale MSAL cache is cleared before React initialises.
await checkAndClearStaleCache();

/**
 * Inner shell — lives inside AuthProvider so it can call msal-react hooks.
 *
 * Responsibilities:
 *   1. Supply live auth state to the router context on every render.
 *   2. Handle auth-state transitions that the static beforeLoad guards cannot
 *      react to (MSAL loading → settled, session expiry mid-session).
 */
function InnerApp() {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = inProgress !== InteractionStatus.None;
  const auth = useAuth();

  // Track previous values so we can detect transitions.
  // Initialise prevLoadingRef to `true` so the very first effect run always
  // sees wasLoading→settled, even when MSAL resolved before the first render
  // (cached tokens / no redirect).  Without this, deep-link restoration and
  // post-auth navigation are skipped for users with a warm cache.
  const prevLoadingRef = useRef(true);
  const prevAuthRef = useRef(isAuthenticated);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const wasAuth = prevAuthRef.current;
    prevLoadingRef.current = isLoading;
    prevAuthRef.current = isAuthenticated;

    // ── MSAL just finished its startup / redirect-processing phase ──────────
    if (wasLoading && !isLoading) {
      if (isAuthenticated) {
        // Check for a deep link that was saved before the loginRedirect call.
        const savedPath = sessionStorage.getItem(AUTH_REDIRECT_KEY);
        if (savedPath) {
          sessionStorage.removeItem(AUTH_REDIRECT_KEY);
          router.history.replace(savedPath);
          return;
        }

        const currentPath = router.state.location.pathname;
        // After a loginRedirect MSAL may navigate back to the page that
        // initiated the login (usually /sign-in) or land at the app root (/).
        // In both cases the user should end up in the app, not the auth shell.
        const isOnAuthPage = currentPath === '/' || currentPath.startsWith('/sign-in');

        if (isOnAuthPage) {
          router.navigate({ to: '/chat', replace: true });
        } else {
          // Normal startup with cached tokens on a deep-link URL.
          // Re-trigger the route so _app's beforeLoad + loader (version check)
          // run with the now-resolved auth context.
          router.history.replace(router.state.location.href);
        }
        return;
      }

      // MSAL finished but user is not authenticated.
      // Force the router to re-run beforeLoad so _app's guard fires and
      // redirects to /sign-in.
      router.history.replace(router.state.location.pathname);
      return;
    }

    // ── Session expired mid-session ─────────────────────────────────────────
    // isAuthenticated transitions true → false while MSAL is idle.
    // (MSAL sets activeAccount=null on InteractionRequiredAuthError.)
    if (wasAuth && !isAuthenticated && !isLoading) {
      router.navigate({ to: '/sign-in', replace: true });
    }
  }, [isLoading, isAuthenticated]);

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth: {
          isAuthenticated,
          isLoading,
          login: auth.login,
          logout: auth.logout,
          getAccessToken: auth.getAccessToken,
          error: auth.error,
        },
      }}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
    </QueryClientProvider>
  </StrictMode>
);
